/* eslint-disable react/no-array-index-key */
import * as React from "react";

import { Button } from "@/newComponents/button/Button";
import { IconBin } from "@/newComponents/icon/Icon";
import { cn } from "@/newComponents/utils/utils";

import { useUIStateStore } from "../../store/uiStateStore";

// Create a simple plus icon component
function IconPlus({ className, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </svg>
  );
}

const defaultValues: string[] = [];

export type MultipleInputProps = {
  label?: string;
  hasError?: boolean;
  smallFont?: boolean;
  customWidth?: boolean;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  addButtonText?: string;
  minItems?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> & {
  "aria-invalid"?: boolean;
};

const FormMultipleInput = React.forwardRef<HTMLInputElement, MultipleInputProps>(
  ({
    className,
    smallFont,
    type = "text",
    hasError,
    customWidth,
    label,
    values = defaultValues,
    onChange,
    placeholder,
    addButtonText = "Add another",
    minItems = 1,
    ...props
  }, ref) => {
    const isTunecore = useUIStateStore(state => state.isTunecore);

    // Check if we're in a form context by looking for aria-invalid (passed by FormControl)
    const hasFormError = props["aria-invalid"] === true || hasError;

    // Ensure we always have at least minItems
    React.useEffect(() => {
      if (values.length < minItems) {
        const newValues = [...values];
        while (newValues.length < minItems) {
          newValues.push("");
        }
        onChange(newValues);
      }
    }, [values, minItems, onChange]);

    const handleValueChange = (index: number, newValue: string) => {
      const newValues = [...values];
      newValues[index] = newValue;
      onChange(newValues);
    };

    const handleAddItem = () => {
      onChange([...values, ""]);
    };

    const handleRemoveItem = (index: number) => {
      if (values.length > minItems) {
        const newValues = values.filter((_, i) => i !== index);
        onChange(newValues);
      }
    };

    const inputClassName = cn(
      "flex font-medium rounded-lg px-2 py-1.5 focus-visible:outline-none transition ease-in-out duration-200",
      customWidth ? "" : "w-full",
      smallFont ? "text-xs" : "text-sm",
      isTunecore
        ? "font-tunecore bg-tunecore-light-grey placeholder:text-white placeholder:text-opacity-35 ring-1 hover:ring-2 ring-tunecore-light-grey hover:ring-tunecore-green focus-visible:ring-tunecore-green"
        : "font-sentric-alt placeholder:text-black placeholder:text-opacity-85 ring-1 hover:ring-2 ring-sentric-dark-background hover:ring-sentric-orange focus-visible:ring-2 focus-visible:ring-sentric-orange",
      hasFormError && (isTunecore ? "ring-red-500" : "ring-red-500"),
      className,
    );

    const renderInputs = () => (
      <div className="space-y-2">
        {values.map((value, index) => (
          <div key={`input-${index}`} className="flex items-center gap-2">
            <input
              type={type}
              className={inputClassName}
              value={value}
              onChange={e => handleValueChange(index, e.target.value)}
              placeholder={placeholder}
              ref={index === 0 ? ref : undefined}
              {...props}
            />
            {values.length > minItems && (
              <Button
                type="button"
                variant="link"
                onClick={() => handleRemoveItem(index)}
                className="min-w-0 p-1"
                icon={<IconBin className={`h-5 w-5 ${isTunecore ? "stroke-white group-hover:stroke-tunecore-green" : "group-hover:stroke-sentric-orange"}`} />}
              />
            )}
          </div>
        ))}

        <Button
          type="button"
          variant="link"
          onClick={handleAddItem}
          className={`"flex items-center gap-1 ${isTunecore ? "" : "text-sentric-orange"}`}
          icon={<IconPlus className="w-5 h-5" />}
          label={addButtonText}
        />
      </div>
    );

    // When used within Shadcn Form structure, don't render the wrapper div and label
    if (label == null || label === "") {
      return renderInputs();
    }

    // When used standalone (with label prop), render the full component with wrapper and label
    return (
      <div className={cn("flex flex-col justify-between", customWidth ? "" : "w-full")}>
        <label
          className={cn(
            "leading-snug self-start mb-2",
            smallFont ? "text-xs" : "text-base",
            isTunecore ? "font-tunecore" : "font-sentric-alt",
          )}
        >
          {label}
        </label>
        {renderInputs()}
      </div>
    );
  },
);

FormMultipleInput.displayName = "FormMultipleInput";

export { FormMultipleInput };
